/**
 * Spacing Control Group Constants
 *
 * Default values, options and configurations for the spacing control group.
 *
 * @package RedfoxThankYou
 * @since 1.0.0
 */

import { __ } from '@wordpress/i18n';

import {
	BREAKPOINT_LABELS,
	COMMON_UNITS,
	DEFAULT_RANGE_SETTINGS,
} from '../common';
import type { EnabledSpacingControls, SpacingPreset } from './types';

/**
 * Re-export common constants for backward compatibility
 */
export const breakpointLabels = BREAKPOINT_LABELS;
export const unitOptions = COMMON_UNITS;
export const defaultRangeSettings = DEFAULT_RANGE_SETTINGS;

/**
 * Default enabled controls configuration
 */
export const DEFAULT_ENABLED_CONTROLS: EnabledSpacingControls = {
	padding: true,
	margin: true,
	responsive: true,
	presets: true,
	boxModel: true,
	autoValues: true,
	advancedOptions: false,
};

/**
 * Default preset values for quick selection
 */
export const defaultPresets: number[] = [
	0, 4, 8, 12, 16, 20, 24, 32, 40, 48, 64,
];

/**
 * Padding preset options
 */
export const paddingPresets: SpacingPreset[] = [
	{ label: __( 'None', 'redfox-thank-you' ), value: '0' },
	{ label: __( 'XS', 'redfox-thank-you' ), value: '4px' },
	{ label: __( 'S', 'redfox-thank-you' ), value: '8px' },
	{ label: __( 'M', 'redfox-thank-you' ), value: '16px' },
	{ label: __( 'L', 'redfox-thank-you' ), value: '24px' },
	{ label: __( 'XL', 'redfox-thank-you' ), value: '32px' },
	{ label: __( 'XXL', 'redfox-thank-you' ), value: '48px' },
];

/**
 * Margin preset options (includes auto)
 */
export const marginPresets: SpacingPreset[] = [
	{ label: __( 'None', 'redfox-thank-you' ), value: '0' },
	{ label: __( 'Auto', 'redfox-thank-you' ), value: 'auto' },
	{ label: __( 'XS', 'redfox-thank-you' ), value: '4px' },
	{ label: __( 'S', 'redfox-thank-you' ), value: '8px' },
	{ label: __( 'M', 'redfox-thank-you' ), value: '16px' },
	{ label: __( 'L', 'redfox-thank-you' ), value: '24px' },
	{ label: __( 'XL', 'redfox-thank-you' ), value: '32px' },
	{ label: __( 'XXL', 'redfox-thank-you' ), value: '48px' },
];

/**
 * Default spacing values
 */
export const defaultSpacingValues = {
	top: '',
	right: '',
	bottom: '',
	left: '',
};

/**
 * Spacing side labels
 */
export const sideLabels = {
	top: __( 'Top', 'redfox-thank-you' ),
	right: __( 'Right', 'redfox-thank-you' ),
	bottom: __( 'Bottom', 'redfox-thank-you' ),
	left: __( 'Left', 'redfox-thank-you' ),
};

/**
 * Box model visualization colors
 */
export const boxModelColors = {
	margin: '#ff6900',
	padding: '#00a32a',
	content: '#0073aa',
	border: '#ddd',
};

/**
 * CSS property mappings for spacing
 */
export const spacingProperties = {
	padding: {
		top: 'padding-top',
		right: 'padding-right',
		bottom: 'padding-bottom',
		left: 'padding-left',
	},
	margin: {
		top: 'margin-top',
		right: 'margin-right',
		bottom: 'margin-bottom',
		left: 'margin-left',
	},
};
